import _ from 'lodash';
import cloneDeep from 'lodash.clonedeep';

import {
  AddResourceAsync,
  AddResourceEntityAsync,
  CopyResourceAsync,
  DeleteResourceAsync,
  GetEntityResourceKeys,
  GetResourceById,
  GetResourceByName,
  GetResourceEntity,
  GetResourceKeys,
  ImportResourceAsync,
  UpdateResourceAsync
} from '../resources';
import {
  ResourceEntity,
  TaskContentEntity,
  TaskEntity
} from '../../../types/game-document/';
import { uuid } from '../../../types/common-helper';
import { FilterResourcePacks } from '../resource-packs';
import { GalleryState } from '../../../components/gallery';
import { GameDocument } from '../../../types/game-document';
import { GetNextAssetNameAsync, MergeAssets } from './index';
import { AddTaskEntityAsync } from './tasks';
import { AddActionToTask } from '../settings';
import {
  AnswerType,
  TaskContent,
  TaskContentAnswer,
  TaskContentForm
} from '../../../types/game-document/entities/task-content';
import { BodyType } from '../../../types/game-document/body-type';
import { ExportedTaskContentData } from '../../../types/game-document/entities/exported-task-content';

/**
 * Adds a new TaskContent to the Game document.
 * @param gameDocument - The Game Document to modify
 * @param name - The Name of the new TaskContent
 * @param description - The Description for the new TaskContent
 * @returns The updated Game Document
 */
export const AddTaskContentAsync = async (
  gameDocument: GameDocument,
  name: string,
  description: string,
  bodyType: BodyType,
  titleResId?: string,
  preMessageResId?: string,
  contentResId?: string,
  id?: string
) => {
  let taskContents = gameDocument?.assets?.taskContents ?? [];
  let taskContentName = await GetNextAssetNameAsync(taskContents, name);
  taskContents.push({
    id: !id ? uuid() : id,
    name: taskContentName,
    description,
    bodyType: bodyType,
    titleResId,
    contentResId,
    preMessageResId
  });
  return MergeAssets(gameDocument, taskContents, 'taskContents');
};

export const AddTaskContentEntityAsync = async (
  gameDocument: GameDocument,
  entity: TaskContentEntity
) => {
  let taskContents = gameDocument.assets.taskContents ?? [];
  let taskContentName = await GetNextAssetNameAsync(
    taskContents,
    entity.name,
    entity.id
  );
  entity.name = taskContentName;
  taskContents.push(entity);
  return MergeAssets(gameDocument, taskContents, 'taskContents');
};

/**
 * Deletes the identified TaskContent from the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param taskContentId - The ID of the TaskContent to delete
 * @returns The updated Game Document
 */
export const DeleteTaskContentAsync = async (
  gameDocument: GameDocument,
  taskContentId: string
) => {
  const taskContents = gameDocument.assets.taskContents ?? [];
  const taskContentIndex = taskContents.findIndex(
    (i) => i.id === taskContentId
  )!;

  if (taskContentIndex != -1) {
    const taskContent = taskContents[taskContentIndex];

    if (taskContent) {
      // Delete all resources associated with the task content
      const deleteResources = async (obj: any) => {
        if (obj) {
          for (const res of GetResourceKeys(obj)) {
            await DeleteResourceAsync(gameDocument, obj[res]);
          }
        }
      };

      // Delete main task content resources
      await deleteResources(taskContent);

      // Delete form and answer resources
      if (taskContent.forms?.length) {
        for (const form of taskContent.forms) {
          await deleteResources(form);
          if (form.answers?.length) {
            for (const answer of form.answers) {
              await deleteResources(answer);
            }
          }

          if (form.customFeedback) {
            await deleteResources(form.customFeedback.allCorrectMessageResId);
            await deleteResources(form.customFeedback.allWrongMessageResId);
            await deleteResources(
              form.customFeedback.partiallyCorrectMessageResId
            );
          }
        }
      }
    }
  }

  taskContents.splice(taskContentIndex, 1);
  return MergeAssets(gameDocument, taskContents, 'taskContents');
};

/**
 * Updates the identified TaskContent in the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param taskContentId - The ID of the TaskContent to update
 * @param taskContent - The updated TaskContent
 * @param renameDuplicate - Auto rename 'name' key if duplicate
 * @constructor
 */
export const UpdateTaskContentAsync = async (
  gameDocument: GameDocument,
  taskContentId: string,
  taskContent: TaskContentEntity,
  renameDuplicate: boolean = true
) => {
  let taskContents = gameDocument.assets.taskContents ?? [];
  let taskContentIndex = taskContents.findIndex((i) => i.id === taskContentId)!;
  if (renameDuplicate)
    taskContent.name = await GetNextAssetNameAsync(
      taskContents,
      taskContent.name,
      taskContent.id
    );
  taskContents[taskContentIndex] = taskContent;
  return MergeAssets(gameDocument, taskContents, 'taskContents');
};

/**
 * Create a copy of the TaskContent in the Game document.
 * @param gameDocument - The Game Document to modify
 * @param taskContentId - The ID of the TaskContent to copy
 * @returns The updated Game Document
 */
export const CopyTaskContentAsync = async (
  gameDocument: GameDocument,
  taskContentId: string
) => {
  const taskContents = gameDocument.assets.taskContents ?? [];
  const contentIndex = taskContents.findIndex((i) => i.id === taskContentId);
  if (contentIndex !== -1) {
    const selectedTaskContent = taskContents[contentIndex];
    const contentCopy: TaskContentEntity = cloneDeep(selectedTaskContent);
    contentCopy.titleResId = uuid();
    contentCopy.contentResId = uuid();
    contentCopy.preMessageResId = uuid();

    // Copy task content resources
    // title, content (intro), and premessage resources
    for (const res of GetResourceKeys(contentCopy)) {
      CopyResourceAsync(
        gameDocument,
        selectedTaskContent[res] as string,
        contentCopy[res] as string
      ).then((response) => {
        gameDocument = response;
      });
    }

    if (contentCopy.forms && selectedTaskContent.forms) {
      const shouldCopyResId: { originalId: string; newId: string }[] = [];
      // Copy task content form resources
      contentCopy.forms.forEach((form, formIndex) => {
        // Regenerate form ID to prevent duplicate FormId in the game document
        form.id = uuid();

        // form question resource
        const originalForm = selectedTaskContent.forms![formIndex];
        form.questionResId = uuid();
        shouldCopyResId.push({
          originalId: originalForm.questionResId ?? '',
          newId: form.questionResId
        });

        if (form.answers && originalForm.answers)
          // form answers resources
          form.answers.forEach((answer, answerIndex) => {
            // Regenerate answer ID to prevent duplicate AnswerId in the game document
            answer.id = uuid();

            answer.answerResId = uuid();
            shouldCopyResId.push({
              originalId: originalForm.answers![answerIndex].answerResId,
              newId: answer.answerResId
            });
          });

        if (form.customFeedback && originalForm.customFeedback) {
          // form custom feedback all correct message
          form.customFeedback.allCorrectMessageResId = uuid();
          shouldCopyResId.push({
            originalId:
              originalForm.customFeedback.allCorrectMessageResId ?? '',
            newId: form.customFeedback.allCorrectMessageResId
          });

          if (form.customFeedback.allCorrectUrlResId !== '') {
            // form custom feedback all correct icon
            form.customFeedback.allCorrectUrlResId = uuid();
            shouldCopyResId.push({
              originalId: originalForm.customFeedback.allCorrectUrlResId ?? '',
              newId: form.customFeedback.allCorrectUrlResId
            });
          }

          // form custom feedback all wrong message
          form.customFeedback.allWrongMessageResId = uuid();
          shouldCopyResId.push({
            originalId: originalForm.customFeedback.allWrongMessageResId ?? '',
            newId: form.customFeedback.allWrongMessageResId
          });

          if (form.customFeedback.allWrongUrlResId !== '') {
            // form custom feedback all wrong icon
            form.customFeedback.allWrongUrlResId = uuid();
            shouldCopyResId.push({
              originalId: originalForm.customFeedback.allWrongUrlResId ?? '',
              newId: form.customFeedback.allWrongUrlResId
            });
          }

          if (form.customFeedback.partiallyCorrectMessageResId !== '') {
            // form custom feedback partially correct message
            form.customFeedback.partiallyCorrectMessageResId = uuid();
            shouldCopyResId.push({
              originalId:
                originalForm.customFeedback.partiallyCorrectMessageResId ?? '',
              newId: form.customFeedback.partiallyCorrectMessageResId
            });
          }

          if (form.customFeedback.partiallyCorrectUrlResId !== '') {
            // form custom feedback partially correct icon
            form.customFeedback.partiallyCorrectUrlResId = uuid();
            shouldCopyResId.push({
              originalId:
                originalForm.customFeedback.partiallyCorrectUrlResId ?? '',
              newId: form.customFeedback.partiallyCorrectUrlResId
            });
          }
        }
      });

      if (shouldCopyResId.length) {
        // execute all resources that should be copied
        await Promise.all(
          shouldCopyResId.map((res) =>
            CopyResourceAsync(gameDocument, res.originalId, res.newId)
          )
        ).then((responses) => (gameDocument = responses[responses.length - 1]));
      }
    }

    contentCopy.id = uuid();
    contentCopy.name += '-copy';
    contentCopy.name = await GetNextAssetNameAsync(
      taskContents,
      contentCopy.name,
      contentCopy.id
    );
    taskContents.push(contentCopy);
  }

  return MergeAssets(gameDocument, taskContents, 'taskContents');
};

/**
 * Get task content by Id from game document local storage.
 * @param taskContentId - The taskContentId you want to search
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTaskContentById = (
  gameDocument: GameDocument | undefined,
  taskContentId: string
) => {
  return gameDocument?.assets?.taskContents?.find(
    (x) => x.id === taskContentId
  );
};

let newResource: ResourceEntity = {
  id: uuid(),
  description: '',
  name: '',
  type: 'text',
  value: ''
};

export const AddNewTaskContentForm = async (
  gameDocument: GameDocument,
  taskContentId: string
) => {
  let taskContent = GetTaskContentById(gameDocument, taskContentId);
  const correctMessageFeedback = {
    isDefaultAllCorrect: true,
    allCorrectUrlResId: '',
    allCorrectMessageResId: uuid()
  };
  const wrongMessageFeedback = {
    isDefaultAllWrong: true,
    allWrongUrlResId: '',
    allWrongMessageResId: uuid()
  };

  let newForm: TaskContentForm = {
    id: uuid(),
    type: 'text',
    answers: [],
    isManualScoring: false,
    // #6565 fix add default custom feedback when create new question
    customFeedback: {
      isDefaultPartiallyCorrect: true,
      partiallyCorrectUrlResId: '',
      partiallyCorrectMessageResId: '',
      ...correctMessageFeedback,
      ...wrongMessageFeedback
    }
  };

  //Add new Question resource to local storage
  newResource.id = uuid();
  await AddResourceEntityAsync(gameDocument, newResource);

  newForm.questionResId = newResource.id;

  if (!taskContent?.forms) {
    taskContent!.forms = [];
  }

  if (!newForm.answers) {
    newForm.answers = [];
  }

  //Add default answer resource to local storage
  newResource.id = uuid();
  await AddResourceEntityAsync(gameDocument, newResource);
  newForm?.answers?.push({
    id: uuid(),
    answerResId: newResource.id
  });

  //Add default correct feedback resource
  await AddResourceEntityAsync(gameDocument, {
    id: correctMessageFeedback.allCorrectMessageResId,
    description: '',
    name: 'All correct feedback message default',
    type: 'text',
    value: ''
  });

  // Add default wrong feedback resource
  await AddResourceEntityAsync(gameDocument, {
    id: wrongMessageFeedback.allWrongMessageResId,
    description: '',
    name: 'All wrong feedback message default',
    type: 'text',
    value: ''
  });

  taskContent?.forms?.push(newForm);

  return await UpdateTaskContentAsync(
    gameDocument,
    taskContentId,
    taskContent!
  );
};
export const AddAnswerTaskContentForm = async (
  gameDocument: GameDocument,
  taskContentId: string,
  questionResId: string
) => {
  let taskContent = GetTaskContentById(gameDocument, taskContentId);

  const questionIndex = taskContent?.forms?.findIndex(
    (x) => x.questionResId === questionResId
  );

  if (taskContent?.forms && questionIndex! > -1) {
    //Add answer resource to local storage
    newResource.id = uuid();
    await AddResourceEntityAsync(gameDocument, newResource);

    if (!taskContent?.forms![questionIndex!]?.answers) {
      taskContent.forms[questionIndex!].answers = [];
    }

    taskContent?.forms![questionIndex!]?.answers?.push({
      id: uuid(),
      answerResId: newResource.id
    });

    return await UpdateTaskContentAsync(
      gameDocument,
      taskContentId,
      taskContent!
    );
  }
};
export const UpdateTaskContentFormAnswers = async (
  gameDocument: GameDocument,
  taskContentId: string,
  formId: string,
  answers: TaskContentAnswer[]
) => {
  let newGameDocument: GameDocument = cloneDeep(gameDocument);
  let taskContent = GetTaskContentById(
    cloneDeep(newGameDocument),
    taskContentId
  );

  let formIndex = taskContent?.forms?.findIndex((x) => x.id === formId);

  taskContent!.forms![formIndex!].answers = answers;

  answers.forEach(async (answer) => {
    let resource = GetResourceEntity(newGameDocument, answer.answerResId);
    resource.value = answer.description;

    newGameDocument = await UpdateResourceAsync(
      newGameDocument,
      resource.id,
      resource
    );
  });

  return await UpdateTaskContentAsync(
    newGameDocument,
    taskContentId,
    taskContent!
  );
};
export const DeleteAnswerTaskContentForm = async (
  gameDocument: GameDocument,
  taskContentId: string,
  questionResId: string,
  answerResId: string
) => {
  let taskContent = GetTaskContentById(gameDocument, taskContentId);

  const questionIndex = taskContent?.forms?.findIndex(
    (x) => x.questionResId === questionResId
  );

  let form = taskContent!.forms![questionIndex!] as TaskContentForm;

  if (form && questionIndex! > -1) {
    const answerIndex = form.answers?.findIndex(
      (x) => x.answerResId === answerResId
    );

    DeleteResourceAsync(
      gameDocument,
      form.answers![answerIndex!].answerResId
    ).then(async (response) => {
      taskContent?.forms![questionIndex!]?.answers?.splice(answerIndex!, 1);

      return await UpdateTaskContentAsync(
        gameDocument,
        taskContentId,
        taskContent!
      );
    });
  }

  return await UpdateTaskContentAsync(
    gameDocument,
    taskContentId,
    taskContent!
  );
};

export const CopyTaskContentFormQuestion = async (
  gameDocument: GameDocument,
  taskContentId: string,
  questionId: string
) => {
  const taskContent = GetTaskContentById(gameDocument, taskContentId);

  if (taskContent && taskContent.forms) {
    const baseFormCopy = taskContent.forms.find(
      (x) => x.questionResId === questionId
    );

    if (baseFormCopy) {
      const formToCopy = {
        ...cloneDeep(baseFormCopy),
        id: uuid(),
        answers:
          baseFormCopy.answers !== undefined
            ? baseFormCopy.answers.map((answer) => ({ ...answer, id: uuid() }))
            : []
      };

      // create new resource and applied the new id
      if (formToCopy.questionResId) {
        const newQuestionResId = uuid();
        CopyResourceAsync(
          gameDocument,
          formToCopy?.questionResId,
          newQuestionResId
        ).then((response) => {
          formToCopy.questionResId = newQuestionResId;
        });
      }

      // create new resource for each answer and applied the new id
      if (formToCopy.answers.length > 0) {
        formToCopy.answers.forEach(async (opt) => {
          if (opt.answerResId) {
            const newOptionsResId = uuid();
            CopyResourceAsync(
              gameDocument,
              opt.answerResId,
              newOptionsResId
            ).then((response) => {
              opt.answerResId = newOptionsResId;
            });
          }
        });
      }

      // create new resource for each custom feedback
      if (formToCopy.customFeedback) {
        const resIds = GetEntityResourceKeys(formToCopy.customFeedback);
        for (const id of resIds) {
          const resId = formToCopy.customFeedback[id] as string;
          if (resId) {
            const newResId = uuid();
            CopyResourceAsync(gameDocument, resId, newResId).then(
              (response) => {
                if (formToCopy.customFeedback) {
                  (formToCopy.customFeedback[id] as string) = newResId;
                }
              }
            );
          }
        }
      }

      taskContent.forms.push(formToCopy);
    }
  }

  return await UpdateTaskContentAsync(
    gameDocument,
    taskContentId,
    taskContent!
  );
};

export const DeleteTaskContentForm = async (
  gameDocument: GameDocument,
  taskContentId: string,
  questionId: string
) => {
  const taskContent = GetTaskContentById(gameDocument, taskContentId);

  const questionIndex = taskContent?.forms?.findIndex(
    (x) => x.questionResId === questionId
  );

  if (!taskContent || !taskContent.forms) return gameDocument;
  if (questionIndex === undefined || questionIndex === -1) return gameDocument;

  const question = taskContent!.forms[questionIndex!];
  const resIds = GetQuestionResourceIds(question);

  resIds.forEach(async (id) => {
    await DeleteResourceAsync(gameDocument, id);
  });

  taskContent?.forms?.splice(questionIndex!, 1);

  return await UpdateTaskContentAsync(
    gameDocument,
    taskContentId,
    taskContent!
  );
};

export const GetQuestionResourceIds = (question: TaskContentForm) => {
  let ids: string[] = [];

  if (question.questionResId) {
    ids.push(question.questionResId);
  }

  if (question.answers) {
    question.answers?.forEach((opt) => {
      if (opt.answerResId) {
        ids.push(opt.answerResId);
      }
    });
  }

  if (question.customFeedback) {
    ids.push(question.customFeedback.allCorrectMessageResId!);
    ids.push(question.customFeedback.partiallyCorrectMessageResId!);
    ids.push(question.customFeedback.allWrongMessageResId!);
  }

  return ids;
};

export const AddDefaultAnswers = (type: AnswerType) => {
  if (['file', 'image', 'video', 'none'].includes(type)) return [];

  let newAnswerResources: ResourceEntity[] = [];

  newAnswerResources.push({
    id: uuid(),
    name: '',
    description: '',
    type: type === 'textarea' ? 'text-long' : 'text',
    value: type === 'time' ? '00:00' : '' // If type === 'time', set default value to 00:00
  });

  if (type === 'checkbox' || type === 'radio' || type === 'number') {
    newAnswerResources.push({
      id: uuid(),
      name: '',
      description: '',
      type: 'text',
      value: ''
    });
  }

  return newAnswerResources;
};

/**
 * Ensures that all 'number' type forms in a task content have 2 answers (min/max range).
 * Legacy data may only have 1 answer (exact number). This function creates a second
 * answer resource automatically if missing.
 * @param gameDocument - The Game Document to check and migrate
 * @param taskContentId - The ID of the TaskContent to check
 * @returns The updated Game Document, or the original if no migration was needed
 */
export const EnsureNumberFormAnswers = async (
  gameDocument: GameDocument,
  taskContentId: string
): Promise<{ gameDocument: GameDocument; migrated: boolean }> => {
  const taskContent = GetTaskContentById(gameDocument, taskContentId);
  if (!taskContent?.forms) return { gameDocument, migrated: false };

  let migrated = false;
  let updatedGameDocument = gameDocument;

  for (const form of taskContent.forms) {
    if (form.type === 'number' && (!form.answers || form.answers.length < 2)) {
      if (!form.answers) {
        form.answers = [];
      }

      // Create missing answer resources
      while (form.answers.length < 2) {
        // Get the value from the first answer's resource (if exists)
        // so the max value defaults to the same as the existing min value
        const existingAnswerValue =
          form.answers.length > 0
            ? ((GetResourceById(
                updatedGameDocument,
                form.answers[0].answerResId
              )?.value as string) ?? '')
            : '';

        const newResourceId = uuid();
        updatedGameDocument = await AddResourceEntityAsync(
          updatedGameDocument,
          {
            id: newResourceId,
            name: 'Answer max value',
            description: '',
            type: 'text',
            value: existingAnswerValue
          }
        );

        form.answers.push({
          id: uuid(),
          answerResId: newResourceId,
          description: existingAnswerValue
        });

        migrated = true;
      }
    }
  }

  if (migrated) {
    updatedGameDocument = await UpdateTaskContentAsync(
      updatedGameDocument,
      taskContentId,
      taskContent
    );
  }

  return { gameDocument: updatedGameDocument, migrated };
};

export const GetTaskContents = (gameDocument: GameDocument) => {
  return gameDocument.assets.taskContents ?? [];
};

/**
 * Create resource for default all correct custom feedback, if it isn't exist, and return the refId
 * @returns
 */
export const AllCorrectCustomFeedback = async (
  gameDocument: GameDocument,
  message: string,
  id: string = uuid()
) => {
  let allCorrectResource = await GetResourceByName(
    gameDocument!,
    'All correct feedback message default'
  );

  if (!allCorrectResource) {
    await AddResourceAsync(
      gameDocument!,
      'All correct feedback message default',
      '',
      'text',
      message,
      id
    ).then((response) => {
      let responseValue = response.resources.find(
        (item) => item.name === 'All correct feedback message default'
      );
      return responseValue?.value ?? '';
    });
  } else {
    return allCorrectResource.value ?? '';
  }

  return '';
};

/**
 * Create resource for default partial correct custom feedback, if it isn't exist, and return the refId
 * @returns
 */
export const PartialCorrectCustomFeedback = async (
  gameDocument: GameDocument,
  message: string,
  id: string = uuid()
) => {
  let allCorrectResource = await GetResourceByName(
    gameDocument!,
    'Partial correct feedback message default'
  );

  if (!allCorrectResource) {
    await AddResourceAsync(
      gameDocument!,
      'Partial correct feedback message default',
      '',
      'text',
      message,
      id
    ).then((response) => {
      let responseValue = response.resources.find(
        (item) => item.name === 'Partial correct feedback message default'
      );
      return responseValue?.value ?? '';
    });
  } else {
    return allCorrectResource.value ?? '';
  }

  return '';
};

/**
 * Create resource for default all wrong custom feedback, if it isn't exist, and return the refId
 * @returns
 */
export const AllWrongCustomFeedback = async (
  gameDocument: GameDocument,
  message: string,
  id: string = uuid()
) => {
  let allCorrectResource = await GetResourceByName(
    gameDocument!,
    'All wrong feedback message default'
  );

  if (!allCorrectResource) {
    await AddResourceAsync(
      gameDocument!,
      'All wrong feedback message default',
      '',
      'text',
      message,
      id
    ).then((response) => {
      let responseValue = response.resources.find(
        (item) => item.name === 'All wrong feedback message default'
      );
      return responseValue?.value ?? '';
    });
  } else {
    return allCorrectResource.value ?? '';
  }
  return '';
};

/**
 * Get default value for custom feedback
 * @returns
 */
export const DefaultCustomFeedback = () => {
  return {
    isDefaultAllCorrect: true,
    allCorrectUrlResId: '',
    allCorrectMessageResId: '',
    isDefaultPartiallyCorrect: true,
    partiallyCorrectUrlResId: '',
    partiallyCorrectMessageResId: '',
    isDefaultAllWrong: true,
    allWrongUrlResId: '',
    allWrongMessageResId: ''
  };
};

export const ExportTaskContents = (
  gameDocument: GameDocument,
  selectedIds: string[]
): ExportedTaskContentData => {
  const selectedTaskContents = gameDocument.assets?.taskContents?.filter(
    (item) => selectedIds.includes(item.id)
  );

  const currentResources = gameDocument.resources;

  let relatedResources: ResourceEntity[] = [];

  selectedTaskContents?.forEach((item) => {
    relatedResources = _.concat(
      relatedResources,
      GetResourcesWithResId(item, currentResources)
    );
  });

  const resourcePacks = FilterResourcePacks(gameDocument, relatedResources);

  return {
    taskContents: selectedTaskContents!,
    resources: relatedResources,
    resourcePacks
  };
};

export const GetResourcesWithResId = (
  taskContent: TaskContent,
  resources: ResourceEntity[]
): ResourceEntity[] => {
  const resIds: string[] = [];

  if (taskContent.titleResId) resIds.push(taskContent.titleResId);
  if (taskContent.preMessageResId) resIds.push(taskContent.preMessageResId);
  if (taskContent.contentResId) resIds.push(taskContent.contentResId);

  if (taskContent.forms) {
    taskContent.forms.forEach((form: TaskContentForm) => {
      if (form.questionResId) resIds.push(form.questionResId);
      if (form.mediaOnlyIdentifierResId)
        resIds.push(form.mediaOnlyIdentifierResId);

      if (form.customFeedback) {
        if (form.customFeedback.allCorrectMessageResId)
          resIds.push(form.customFeedback.allCorrectMessageResId);
        if (form.customFeedback.partiallyCorrectMessageResId)
          resIds.push(form.customFeedback.partiallyCorrectMessageResId);
        if (form.customFeedback.allWrongMessageResId)
          resIds.push(form.customFeedback.allWrongMessageResId);
      }

      if (form.medias && form.medias.length > 0) {
        form.medias.forEach((item) => resIds.push(item));
      }

      if (form.answers && form.answers.length > 0) {
        form.answers.forEach((item) => resIds.push(item.answerResId));
      }
    });
  }

  return _.filter(resources, (resource) => _.includes(resIds, resource.id));
};

export const ImportTaskContents = async (
  gameDocument: GameDocument,
  exportedTaskContentData: ExportedTaskContentData,
  createLinkedTasks: boolean = false
) => {
  // Work on a deep clone for atomicity — only return the clone on full success
  let clonedDoc = _.cloneDeep(gameDocument);
  const taskContents = clonedDoc.assets.taskContents;
  const exportedResources = exportedTaskContentData.resources;
  const exportedResourcePacks = exportedTaskContentData.resourcePacks;

  try {
    for (const taskContent of exportedTaskContentData.taskContents) {
      // Deep copy the taskContent object
      const newTaskContent = _.cloneDeep(taskContent);
      const newTaskContentId = uuid();
      newTaskContent.id = newTaskContentId;

      // Update TaskContent properties with new resIds
      newTaskContent.titleResId = await ImportResourceAsync(
        clonedDoc,
        taskContent.titleResId,
        exportedResources,
        exportedResourcePacks
      );
      newTaskContent.preMessageResId = await ImportResourceAsync(
        clonedDoc,
        taskContent.preMessageResId,
        exportedResources,
        exportedResourcePacks
      );
      newTaskContent.contentResId = await ImportResourceAsync(
        clonedDoc,
        taskContent.contentResId,
        exportedResources,
        exportedResourcePacks
      );

      // Update each form and its custom feedback with new resIds
      if (newTaskContent.forms) {
        for (const exportedForm of newTaskContent.forms) {
          const form: TaskContentForm = exportedForm;
          form.questionResId = await ImportResourceAsync(
            clonedDoc,
            form.questionResId,
            exportedResources,
            exportedResourcePacks
          );
          if (form.customFeedback) {
            form.customFeedback.allCorrectMessageResId =
              await ImportResourceAsync(
                clonedDoc,
                form.customFeedback.allCorrectMessageResId,
                exportedResources,
                exportedResourcePacks
              );
            form.customFeedback.partiallyCorrectMessageResId =
              await ImportResourceAsync(
                clonedDoc,
                form.customFeedback.partiallyCorrectMessageResId,
                exportedResources,
                exportedResourcePacks
              );
            form.customFeedback.allWrongMessageResId =
              await ImportResourceAsync(
                clonedDoc,
                form.customFeedback.allWrongMessageResId,
                exportedResources,
                exportedResourcePacks
              );
          }

          if (form.medias && form.medias.length > 0) {
            const medias = form.medias;
            form.medias = [];
            for (const media of medias) {
              const mediaId = await ImportResourceAsync(
                clonedDoc,
                media,
                exportedResources,
                exportedResourcePacks
              );
              form.medias.push(mediaId!);
            }
          }

          if (form.answers && form.answers.length > 0) {
            const answers = form.answers;
            form.answers = [];

            for (const answer of answers) {
              const answerResId = await ImportResourceAsync(
                clonedDoc,
                answer.answerResId,
                exportedResources,
                exportedResourcePacks
              );
              form.answers.push({
                ...answer,
                answerResId: answerResId!
              });
            }
          }
        }
      }

      // Add the newly created TaskContent to the array
      taskContents!.push(newTaskContent);
      clonedDoc = MergeAssets(clonedDoc, taskContents!, 'taskContents');

      // Create linked Task if requested
      if (createLinkedTasks) {
        try {
          // Get the title value from the imported task content's title resource
          const titleValue = newTaskContent.titleResId
            ? (GetResourceById(clonedDoc, newTaskContent.titleResId)?.value ??
              '')
            : '';

          // Create task title resource
          const taskTitleResId = uuid();
          clonedDoc = await AddResourceEntityAsync(clonedDoc, {
            id: taskTitleResId,
            name: 'Title',
            description: '',
            type: 'text',
            value: titleValue
          });

          // Create task image resources using default icons from game settings
          const taskImageResId = uuid();
          const taskCompleteImageResId = uuid();

          let availableIconUrl =
            'https://cdn.catalystglobal.games/resources/map-task.png';
          let completeIconUrl =
            'https://cdn.catalystglobal.games/resources/map-task--complete.png';

          if (clonedDoc.settings?.designer) {
            const availableIconResId =
              clonedDoc.settings.designer.defaultTaskAvailableIconResId;
            if (availableIconResId) {
              const res = GetResourceById(clonedDoc, availableIconResId);
              if (res?.value) availableIconUrl = res.value;
            }

            const completeIconResId =
              clonedDoc.settings.designer.defaultTaskCompletedIconResId;
            if (completeIconResId) {
              const res = GetResourceById(clonedDoc, completeIconResId);
              if (res?.value) completeIconUrl = res.value;
            }
          }

          clonedDoc = await AddResourceEntityAsync(clonedDoc, {
            id: taskImageResId,
            name: taskImageResId,
            description: 'map-task.png',
            type: 'image',
            value: availableIconUrl,
            size: 15150,
            category: 'Task icons'
          });

          clonedDoc = await AddResourceEntityAsync(clonedDoc, {
            id: taskCompleteImageResId,
            name: taskCompleteImageResId,
            description: 'map-task--complete.png',
            type: 'image',
            value: completeIconUrl,
            size: 10909,
            category: 'Task icons'
          });

          // Determine hideWhenComplete from designer settings
          const hideWhenComplete =
            clonedDoc.settings?.designer
              ? !Boolean(
                  clonedDoc.settings.designer.taskCompletionVisibility
                )
              : false;

          // Determine isTitleVisible from designer settings
          const isTitleVisible =
            clonedDoc.settings?.designer?.defaultTaskTitleVisibility ?? false;

          // Build the new Task entity
          const newTask: TaskEntity = {
            id: uuid(),
            name: newTaskContent.name,
            description: newTaskContent.description,
            titleResId: taskTitleResId,
            imageResId: taskImageResId,
            completeImageResId: taskCompleteImageResId,
            taskContentId: newTaskContentId,
            hideWhenComplete,
            isTitleVisible
          };

          // Add default event actions (e.g., giveScore)
          AddActionToTask(clonedDoc, newTask);

          // Add task to the document
          clonedDoc = await AddTaskEntityAsync(clonedDoc, newTask);
        } catch (taskError) {
          throw new Error(
            `Failed to create linked Task for: "${newTaskContent.name}". Import has been rolled back.`
          );
        }
      }
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      'Failed to import task content. Import has been rolled back.'
    );
  }

  return clonedDoc;
};

export const GetFormMedia = (
  gameDocument: GameDocument,
  form: TaskContentForm
) => {
  let galleryItems: GalleryState[] = [];

  if (form.medias) {
    form?.medias.forEach((resourceId) => {
      let resource = GetResourceById(gameDocument, resourceId);

      galleryItems.push({
        id: resource?.id,
        name: resource?.name,
        fileType: resource?.type,
        url: resource?.value
      });
    });
  }
  return galleryItems;
};
